import type { Task } from '@travel-planner/shared/schemas/tasks';
import { format, parseISO } from 'date-fns';

// "Thursday, October 23" — legacy day-of-week + month + day format.
const formatLongDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, 'EEEE, MMMM d');
  } catch {
    return '—';
  }
};

export type ExpireOnDisplay =
  | { kind: 'absolute'; text: string }
  | { kind: 'relative'; text: string };

// What the "Expire On" cell shows, mirroring the legacy Task Management
// table (inline text, not a tooltip — see the screenshot):
//   - custom_date          → "Due date is the custom date" (always inline,
//                             even when an absolute expireOn is stored)
//   - has an expireOn date → the absolute date ("Thursday, October 23"),
//                            coloured red/amber when past/soon
//   - otherwise (relative) → "Due date is {direction} the {reference}"
// `kind` lets the cell pick styling: `absolute` gets the past/soon colour
// treatment; `relative` is plain muted text.
export const getExpireOnDisplay = (
  task: Pick<Task, 'timerReference' | 'timerDirection' | 'expireOn'>
): ExpireOnDisplay => {
  if (task.timerReference === 'custom_date') {
    return { kind: 'relative', text: 'Due date is the custom date' };
  }

  if (task.expireOn) {
    return { kind: 'absolute', text: formatLongDate(task.expireOn) };
  }

  const reference = task.timerReference.split('_').join(' ');
  const direction = task.timerDirection ?? '';
  const text = `Due date is ${direction ? `${direction} ` : ''}the ${reference}`;
  return { kind: 'relative', text };
};
